import React, { useState, useEffect, useContext } from 'react';
import FileBUUploadModal from './FileBUUploadModal';
import { FaRegFilePdf, FaFilePowerpoint, FaRegFileExcel, FaFileWord, FaFile, FaFolderOpen } from 'react-icons/fa';
import { ModeContext } from './ModeContext';
import {
  Box,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Collapse,
  Divider
} from '@mui/material';
import { ExpandLess, ExpandMore, ControlPoint, SortByAlpha } from '@mui/icons-material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { LoadToCosmos } from './SendToCosmos';

const SettingsDocUp = () => {
  const [loading, setLoading] = useState(true);
  const [openItems, setOpenItems] = useState({});
  const [openCategories, setOpenCategories] = useState({});
  const [openFilter2, setOpenFilter2] = useState({});
  const [sortOrder, setSortOrder] = useState({}); // カテゴリごとのソート順（'asc' or 'desc'）
  const [sortOrderBu, setSortOrderBu] = useState({}); // bu内のcategoryソート順
  const [sortOrderFilter2, setSortOrderFilter2] = useState({}); // filter2ごとのソート順
  const [sortKeyFilter2, setSortKeyFilter2] = useState({}); // filter2ごとのソートキー（'filename' or 'timestamp'）
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const { filebuList, setFilebuList } = useContext(ModeContext);

  // 事業部ごと・カテゴリごと・filter2ごとにデータを整理
  const fetchFileListbu = async () => {
    try {
      const uppayload = { id: 'all', container: 'con-concierge-filebu' };
      const items = await LoadToCosmos(uppayload);

      // フラットなデータを bu → category → filter2 → ファイル一覧 にグルーピング
      const grouped = {};
      (items.item || []).forEach((item) => {
        const buName = item.bu || '未分類';
        const catName = item.category || '未分類';
        const f2Name = item.filter2 || '未分類';

        if (!grouped[buName]) grouped[buName] = {};
        if (!grouped[buName][catName]) grouped[buName][catName] = {};
        if (!grouped[buName][catName][f2Name]) grouped[buName][catName][f2Name] = [];

        grouped[buName][catName][f2Name].push(item);
      });

      setFilebuList(grouped);
    } catch (error) {
      console.error('データ取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFileListbu();
  }, []);

  const handleToggle = (id) => {
    setOpenItems((prev) => ({ ...prev, [id]: !prev[id] }));
    // 初回展開時は昇順ソートを設定
    setSortOrderBu((prev) => {
      if (!prev[id]) return { ...prev, [id]: 'asc' };
      return prev;
    });
  };

  // bu内のcategoryを名前順でソートするトグル
  const handleSortToggleBu = (buKey, e) => {
    e.stopPropagation();
    setSortOrderBu((prev) => ({
      ...prev,
      [buKey]: prev[buKey] === 'asc' ? 'desc' : 'asc',
    }));
  };

  // categoryエントリをソートして返す
  const getSortedCategoryEntries = (categories, buKey) => {
    const entries = Object.entries(categories);
    const order = sortOrderBu[buKey];
    if (!order) return entries;
    return entries.sort(([a], [b]) =>
      order === 'asc' ? a.localeCompare(b, 'ja') : b.localeCompare(a, 'ja')
    );
  };

  const toggleCategory = (categoryKey) => {
    setOpenCategories((prev) => ({ ...prev, [categoryKey]: !prev[categoryKey] }));
    // 初回展開時は昇順ソートを設定
    setSortOrder((prev) => {
      if (!prev[categoryKey]) return { ...prev, [categoryKey]: 'asc' };
      return prev;
    });
  };

  // カテゴリ内のfilter2を名前順でソートするトグル
  const handleSortToggle = (categoryKey, e) => {
    e.stopPropagation();
    setSortOrder((prev) => ({
      ...prev,
      [categoryKey]: prev[categoryKey] === 'asc' ? 'desc' : 'asc',
    }));
  };

  // filter2エントリをソートして返す
  const getSortedFilter2Entries = (filter2Groups, categoryKey) => {
    const entries = Object.entries(filter2Groups);
    const order = sortOrder[categoryKey];
    if (!order) return entries;
    return entries.sort(([a], [b]) =>
      order === 'asc' ? a.localeCompare(b, 'ja') : b.localeCompare(a, 'ja')
    );
  };

  const toggleFilter2 = (filter2Key) => {
    setOpenFilter2((prev) => ({ ...prev, [filter2Key]: !prev[filter2Key] }));
    // 初回展開時は昇順ソートを設定
    setSortOrderFilter2((prev) => {
      if (!prev[filter2Key]) return { ...prev, [filter2Key]: 'asc' };
      return prev;
    });
  };

  // filter2内のファイルをソートするトグル（ファイル名 or timestamp）
  const handleSortToggleFilter2 = (filter2Key, key, e) => {
    e.stopPropagation();
    setSortKeyFilter2((prev) => {
      const currentKey = prev[filter2Key] || 'filename';
      if (currentKey === key) {
        // 同じキーなら昇順/降順を切り替え
        setSortOrderFilter2((prevOrder) => ({
          ...prevOrder,
          [filter2Key]: prevOrder[filter2Key] === 'asc' ? 'desc' : 'asc',
        }));
        return prev;
      }
      // 異なるキーに切り替え → 昇順にリセット
      setSortOrderFilter2((prevOrder) => ({
        ...prevOrder,
        [filter2Key]: 'asc',
      }));
      return { ...prev, [filter2Key]: key };
    });
  };

  // ファイル一覧をソートして返す
  const getSortedMessages = (messages, filter2Key) => {
    const order = sortOrderFilter2[filter2Key];
    const key = sortKeyFilter2[filter2Key] || 'filename';
    if (!order) return messages;
    return [...messages].sort((a, b) => {
      if (key === 'timestamp') {
        const ta = a.timestamp || 0;
        const tb = b.timestamp || 0;
        return order === 'asc' ? ta - tb : tb - ta;
      }
      return order === 'asc'
        ? (a.filename || '').localeCompare(b.filename || '', 'ja')
        : (b.filename || '').localeCompare(a.filename || '', 'ja');
    });
  };

  // timestampを表示用にフォーマット
  const formatTimestamp = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
      + ' ' + d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  const handleOpenModal = (bu, category, filter2) => {
    setSelectedFile({ bu, category: category || '', filter2: filter2 || '' });
    setIsNewModalOpen(true);
  };

  const handleFileClick = (message, bu) => {
    setSelectedFile({ ...message, bu });
    setIsNewModalOpen(true);
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FaFile style={{ marginRight: 8 }} />;
    if (filename.endsWith('.pdf')) return <FaRegFilePdf style={{ marginRight: 8, color: '#D24726' }} />;
    if (filename.endsWith('.xls') || filename.endsWith('.xlsx')) return <FaRegFileExcel style={{ marginRight: 8, color: 'green' }} />;
    if (filename.endsWith('.doc') || filename.endsWith('.docx')) return <FaFileWord style={{ marginRight: 8, color: 'blue' }} />;
    if (filename.endsWith('.ppt') || filename.endsWith('.pptx')) return <FaFilePowerpoint style={{ marginRight: 8, color: '#D24726' }} />;
    return <FaFile style={{ marginRight: 8 }} />;
  };

return (
  <Box sx={{ height: '80vh', overflow: 'auto', p: 2 }}>
    <Typography variant="h5" component="div" sx={{ color: '#1976d2', mb: 2 }}>
      <FileUploadIcon /> 取扱説明書_取込み
    </Typography>

    {loading ? (
      <CircularProgress />
    ) : (
      <List dense>
        <Divider />
        {Object.entries(filebuList || {}).map(([buName, categories], index) => (
          <React.Fragment key={buName}>
            {index !== 0 && <Divider />}
            <ListItem button onClick={() => handleToggle(buName)} sx={{ py: 0.5, bgcolor: '#f5f5f5' }}>
              <ListItemText primary={buName} primaryTypographyProps={{ fontWeight: 'bold' }} />
              <IconButton
                edge="end"
                size="small"
                sx={{ mr: 0.5 }}
                onClick={(e) => handleSortToggleBu(buName, e)}
                title={sortOrderBu[buName] === 'asc' ? '降順にソート' : '昇順にソート'}
              >
                <SortByAlpha sx={{ fontSize: 18 }} color={sortOrderBu[buName] ? 'primary' : 'disabled'} />
              </IconButton>
              <IconButton
                edge="end"
                size="small"
                sx={{ mr: 1 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenModal(buName);
                }}
              >
                <ControlPoint fontSize="small" />
              </IconButton>
              {openItems[buName] ? <ExpandLess /> : <ExpandMore />}
            </ListItem>

            <Collapse in={openItems[buName]} timeout="auto" unmountOnExit>
              <List component="div" disablePadding dense>
                {getSortedCategoryEntries(categories, buName).map(([category, filter2Groups]) => (
                  <React.Fragment key={category}>
                    <ListItem
                      button
                      sx={{ pl: 4, py: 0.3 }}
                      onClick={() => toggleCategory(`${buName}-${category}`)}
                    >
                      <FaFolderOpen style={{ marginRight: 8, color: '#FFCC00', fontSize: '20px' }} />
                      <ListItemText primary={category || '未分類'} primaryTypographyProps={{ fontSize: '0.9rem' }} />
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{ mr: 0.5 }}
                        onClick={(e) => handleSortToggle(`${buName}-${category}`, e)}
                        title={sortOrder[`${buName}-${category}`] === 'asc' ? '降順にソート' : '昇順にソート'}
                      >
                        <SortByAlpha sx={{ fontSize: 16 }} color={sortOrder[`${buName}-${category}`] ? 'primary' : 'disabled'} />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="small"
                        sx={{ mr: 0.5 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenModal(buName, category);
                        }}
                      >
                        <ControlPoint sx={{ fontSize: 18 }} />
                      </IconButton>
                      {openCategories[`${buName}-${category}`] ? <ExpandLess /> : <ExpandMore />}
                    </ListItem>

                    <Collapse in={openCategories[`${buName}-${category}`]} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding dense>
                        {getSortedFilter2Entries(filter2Groups, `${buName}-${category}`).map(([filter2, messages]) => (
                          <React.Fragment key={filter2}>
                            <ListItem
                              button
                              sx={{ pl: 6, py: 0.2 }}
                              onClick={() => toggleFilter2(`${buName}-${category}-${filter2}`)}
                            >
                              <FaFolderOpen style={{ marginRight: 8, color: '#90CAF9', fontSize: '18px' }} />
                              <ListItemText primary={filter2 || '未分類'} primaryTypographyProps={{ fontSize: '0.85rem' }} />
                              <IconButton
                                edge="end"
                                size="small"
                                sx={{ mr: 0.5 }}
                                onClick={(e) => handleSortToggleFilter2(`${buName}-${category}-${filter2}`, 'filename', e)}
                                title="ファイル名でソート"
                              >
                                <SortByAlpha sx={{ fontSize: 16 }} color={sortKeyFilter2[`${buName}-${category}-${filter2}`] === 'filename' || (!sortKeyFilter2[`${buName}-${category}-${filter2}`] && sortOrderFilter2[`${buName}-${category}-${filter2}`]) ? 'primary' : 'disabled'} />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                sx={{ mr: 0.5 }}
                                onClick={(e) => handleSortToggleFilter2(`${buName}-${category}-${filter2}`, 'timestamp', e)}
                                title="日時でソート"
                              >
                                <Typography sx={{ fontSize: 11, fontWeight: 'bold', lineHeight: 1 }} color={sortKeyFilter2[`${buName}-${category}-${filter2}`] === 'timestamp' ? 'primary' : 'text.disabled'}>日時</Typography>
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                sx={{ mr: 0.5 }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenModal(buName, category, filter2);
                                }}
                              >
                                <ControlPoint sx={{ fontSize: 16 }} />
                              </IconButton>
                              {openFilter2[`${buName}-${category}-${filter2}`] ? <ExpandLess /> : <ExpandMore />}
                            </ListItem>

                            <Collapse in={openFilter2[`${buName}-${category}-${filter2}`]} timeout="auto" unmountOnExit>
                              <List component="div" disablePadding dense>
                                {getSortedMessages(messages, `${buName}-${category}-${filter2}`).map((message) => (
                                  <ListItem
                                    key={message.id}
                                    button
                                    sx={{ pl: 8, py: 0, minHeight: '26px' }}
                                    onClick={() => handleFileClick(message, buName)}
                                  >
                                    {getFileIcon(message.filename)}
                                    <ListItemText
                                      primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                          <Typography sx={{ fontSize: '0.8rem', flexShrink: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {message.filename}
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', ml: 2, flexShrink: 0, whiteSpace: 'nowrap' }}>
                                            {formatTimestamp(message.timestamp)}
                                          </Typography>
                                        </Box>
                                      }
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                          </React.Fragment>
                        ))}
                      </List>
                    </Collapse>
                  </React.Fragment>
                ))}
              </List>
            </Collapse>
          </React.Fragment>
        ))}
      </List>
    )}

    <FileBUUploadModal
      open={isNewModalOpen}
      onClose={() => setIsNewModalOpen(false)}
      fileData={selectedFile || {}}
      filebuList={filebuList}
      type='cust'
      onUpdated={fetchFileListbu}
    />
  </Box>
);
};

export default SettingsDocUp;